import { Header } from "@/components/header";
import { Hero } from "@/components/hero";
import { VillageChiefSection } from "@/components/village-chief-section";
import { CommunitySection } from "@/components/community-section";
import { StatisticsSection } from "@/components/statistics-section";
import { ServicesSection } from "@/components/services-section";
import { GovernmentLinksSection } from "@/components/government-links-section";
import { Footer } from "@/components/footer";
import {
  useVillageChief,
  useResidents,
  useStatistics,
  useServices,
  useGovernmentLinks,
  useSiteAlerts,
  type SiteAlert,
} from "@/hooks/use-supabase-data";
import { SiteAlertModal } from "@/components/site-alert-modal";
import { useEffect, useMemo, useState } from "react";

export default function Home() {
  const { chief, loading: chiefLoading } = useVillageChief();
  const { residents, loading: residentsLoading } = useResidents();
  const { statistics, loading: statisticsLoading } = useStatistics();
  const { services, loading: servicesLoading } = useServices();
  const { links, loading: linksLoading } = useGovernmentLinks();
  const { alerts } = useSiteAlerts();

  const [activeAlert, setActiveAlert] = useState<SiteAlert | null>(null);

  const scheduledAlert = useMemo(() => {
    if (!alerts || alerts.length === 0) return null;
    const now = new Date();
    return alerts
      .filter((alert) => {
        if (!alert.is_active) return false;
        const startsAt = alert.start_at ? new Date(alert.start_at) : null;
        const endsAt = alert.end_at ? new Date(alert.end_at) : null;
        if (startsAt && startsAt > now) return false;
        if (endsAt && endsAt < now) return false;
        return true;
      })
      .sort((a, b) => {
        const startA = a.start_at ? new Date(a.start_at).getTime() : 0;
        const startB = b.start_at ? new Date(b.start_at).getTime() : 0;
        return startB - startA;
      })[0] ?? null;
  }, [alerts]);

  useEffect(() => {
    setActiveAlert(scheduledAlert);
  }, [scheduledAlert]);

  const handleDismissAlert = (alertId: string) => {
    if (activeAlert?.id === alertId) {
      setActiveAlert(null);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="page-home">
      <Header />
      <SiteAlertModal alert={activeAlert} onDismiss={handleDismissAlert} />
      <Hero />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <section id="leadership">
          <VillageChiefSection chief={chief} isLoading={chiefLoading} />
        </section>
        <section id="statistics">
          <StatisticsSection statistics={statistics} isLoading={statisticsLoading} />
        </section>
        <section id="community">
          <CommunitySection residents={residents} isLoading={residentsLoading} />
        </section>
        <section id="services">
          <ServicesSection services={services} isLoading={servicesLoading} />
        </section>
        <section id="links">
          <GovernmentLinksSection links={links} isLoading={linksLoading} />
        </section>
      </main>

      <Footer />
    </div>
  );
}
